import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Crown, Check, X, Sparkles, ShieldCheck, Zap, Bot, FileStack, Infinity as InfinityIcon, AlertCircle } from 'lucide-react';
import { api, formatApiErrorDetail } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PricingPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    api.get('/payments/config').then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const startCheckout = async () => {
    if (!user || typeof user !== 'object') {
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    setLoading(true); setError('');

    try {
      const { data: order } = await api.post('/payments/create-order', { plan: 'pro_monthly' });

      if (order.mock_mode) {
        // MOCK MODE: skip Razorpay checkout, go straight to verify
        toast.info('Mock payment mode: simulating payment...');
        await new Promise((r) => setTimeout(r, 800));
        const { data } = await api.post('/payments/verify', {
          razorpay_payment_id: `pay_MOCK_${Date.now()}`,
          razorpay_order_id: order.order_id,
        });
        await refresh();
        toast.success(`Pro activated! Trial ends ${new Date(data.pro_until).toLocaleDateString()}`);
        navigate('/dashboard');
        return;
      }

      // REAL Razorpay flow
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error('Failed to load Razorpay');
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: 'ValutDocu Pro',
        description: '7-day free trial, then ₹149/month',
        prefill: { name: user.name, email: user.email },
        theme: { color: '#F5A623' },
        handler: async (response) => {
          try {
            await api.post('/payments/verify', {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            await refresh();
            toast.success('Pro activated!');
            navigate('/dashboard');
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(detail);
      toast.error(detail);
    } finally { setLoading(false); }
  };

  const isPro = user && typeof user === 'object' && user.plan === 'pro';

  return (
    <div data-testid="pricing-page" className="vault-grid-bg min-h-[calc(100vh-4rem)] py-16">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.2em] uppercase font-mono text-vault-gold mb-3">Pricing</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tighter">
            Free forever. <span className="gold-text">Pro when you need it.</span>
          </h1>
          <p className="mt-5 text-gray-400 max-w-xl mx-auto">
            ValutDocu is built to be free. Pro unlocks AI tools, no ads, and bigger files —
            for less than half the cost of iLovePDF.
          </p>
        </div>

        {config?.mock_mode && (
          <div data-testid="mock-banner" className="max-w-2xl mx-auto mb-10 bg-vault-gold/5 border border-vault-gold/30 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-vault-gold shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-vault-gold font-semibold">Demo mode active</p>
              <p className="text-gray-400 mt-1">Razorpay API keys not yet configured. Clicking "Start trial" will simulate a successful payment and grant 7-day Pro access. Add real keys to <code className="bg-vault-bg px-1.5 py-0.5 rounded text-xs">backend/.env</code> to enable live payments.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* FREE */}
          <div data-testid="free-plan-card" className="tool-card rounded-2xl p-8 relative">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-gray-400" />
              <span className="text-xs tracking-[0.2em] uppercase font-mono text-gray-400">Free</span>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-5xl font-heading font-bold tracking-tighter">₹0</span>
              <span className="text-gray-500 text-sm">forever</span>
            </div>
            <p className="text-gray-400 text-sm mb-6">All 33 standard tools. No signup needed.</p>
            <ul className="space-y-3 mb-8">
              <Feat ok>All 33 PDF tools</Feat>
              <Feat ok>Unlimited file count</Feat>
              <Feat ok>No watermarks ever</Feat>
              <Feat ok>100MB per file</Feat>
              <Feat ok>3 AI uses per day</Feat>
              <Feat ok={false}>Unlimited AI</Feat>
              <Feat ok={false}>2GB file size</Feat>
              <Feat ok={false}>Priority processing</Feat>
              <Feat ok={false}>API access</Feat>
            </ul>
            <Link to="/" className="btn-ghost w-full text-center block">Continue free</Link>
          </div>

          {/* PRO */}
          <div data-testid="pro-plan-card" className="glass-panel rounded-2xl p-8 relative overflow-hidden border-vault-gold/40">
            <div className="absolute -top-16 -right-16 w-40 h-40 bg-vault-gold/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute top-6 right-6 bg-vault-gold text-vault-paper text-[10px] font-mono tracking-[0.18em] font-bold px-2.5 py-1 rounded-full">
              MOST POPULAR
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-vault-gold" />
              <span className="text-xs tracking-[0.2em] uppercase font-mono text-vault-gold">Pro</span>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-5xl font-heading font-bold gold-text tracking-tighter">₹149</span>
              <span className="text-gray-400 text-sm">/month</span>
            </div>
            <p className="text-gray-400 text-sm mb-6">
              <span className="text-vault-gold font-semibold">7-day free trial</span> · cancel anytime
            </p>
            <ul className="space-y-3 mb-8">
              <Feat ok>Everything in Free</Feat>
              <Feat ok highlight>4 AI tools — unlimited</Feat>
              <Feat ok highlight>2GB file size</Feat>
              <Feat ok highlight>Priority processing queue</Feat>
              <Feat ok highlight>No ads</Feat>
              <Feat ok highlight>Batch processing</Feat>
              <Feat ok highlight>API access</Feat>
              <Feat ok highlight>Email support</Feat>
            </ul>
            {isPro ? (
              <div data-testid="already-pro-state" className="btn-primary w-full text-center inline-flex justify-center items-center gap-2">
                <Crown className="w-4 h-4" /> You're on Pro
              </div>
            ) : (
              <button
                data-testid="start-trial-button"
                onClick={startCheckout}
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? 'Starting trial…' : 'Start 7-day free trial'}
              </button>
            )}
            {error && <p data-testid="pricing-error" className="text-sm text-red-400 mt-3 text-center">{error}</p>}
            <p className="text-xs text-gray-500 mt-3 text-center">Compare: iLovePDF Pro is $7+/mo (~₹580)</p>
          </div>
        </div>

        {/* Comparison */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-center mb-8">
            Why pay <span className="gold-text">3× less</span> for more?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { Icon: Bot, t: 'AI tools built-in', d: 'iLovePDF has none. ValutDocu Pro includes Summarize, Chat, Translate, and Extract.' },
              { Icon: Zap, t: '3× faster engine', d: 'Native PyMuPDF + Python optimization. Compresses 50MB PDFs in seconds, not minutes.' },
              { Icon: ShieldCheck, t: 'True privacy', d: '60-min auto-delete, no telemetry, no analytics. Your documents leave no trace.' },
            ].map(({ Icon, t, d }, i) => (
              <div key={i} className="tool-card rounded-xl p-5">
                <Icon className="w-6 h-6 text-vault-gold mb-3" />
                <h4 className="font-heading font-semibold mb-1">{t}</h4>
                <p className="text-sm text-gray-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Feat({ ok, highlight, children }) {
  return (
    <li className="flex items-start gap-2.5 text-sm">
      {ok ? (
        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? 'text-vault-gold' : 'text-green-400'}`} />
      ) : (
        <X className="w-4 h-4 mt-0.5 shrink-0 text-gray-600" />
      )}
      <span className={ok ? (highlight ? 'text-white font-medium' : 'text-gray-300') : 'text-gray-600 line-through'}>
        {children}
      </span>
    </li>
  );
}
